import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { giftLidPositionLabel } from "../GiftBoxLidEditor";
import { ProductImageWithFallbacks } from "../ProductImageWithFallbacks";
import { BtnContent } from "../BtnIcon";
import {
  IoAddOutline,
  IoCartOutline,
  IoCardOutline,
  IoCloseOutline,
  IoCreateOutline,
  IoRemoveOutline,
  IoTrashOutline,
} from "react-icons/io5";

export default function CartDrawer({
  open,
  onClose,
  cart,
  removeFromCart,
  updateQuantity,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id);
      return;
    }
    updateQuantity(id, newQuantity);
  };

  const customiseState = (item) => ({
    product: item,
    editCartLine: true,
    giftLidMessage: item.giftLidMessage || "",
    giftLidLayout: item.giftLidLayout,
    giftLidTextColor: item.giftLidTextColor,
    returnTo: location.pathname || "/shop",
  });

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            type="button"
            className="lux-drawer__backdrop"
            aria-label="Close cart"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="lux-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Shopping cart preview"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
          >
            <header className="lux-drawer__head">
              <h2>Your cart</h2>
              <button type="button" className="lux-drawer__close" onClick={onClose}>
                <BtnContent icon={IoCloseOutline} iconSize={14}>
                  Close
                </BtnContent>
              </button>
            </header>
            {cart.length === 0 ? (
              <p className="lux-drawer__empty">Your cart is empty.</p>
            ) : (
              <>
                <ul className="lux-drawer__list">
                  {cart.map((item) => (
                    <li key={item.id} className="lux-drawer__line">
                      <div className="lux-drawer__thumb">
                        <ProductImageWithFallbacks
                          urls={item.images}
                          alt=""
                          loading="lazy"
                        />
                      </div>
                      <div className="lux-drawer__line-body">
                        <p className="lux-drawer__name">{item.name}</p>
                        {item.giftLidMessage ? (
                          <p className="lux-drawer__lid small text-muted">
                            Gift box: {item.giftLidMessage}
                            {item.giftLidLayout ? (
                              <span className="d-block lux-drawer__lid-meta">
                                {giftLidPositionLabel(item.giftLidLayout)} ·{" "}
                                {Number(item.giftLidLayout.scale).toFixed(2)}×
                                {item.giftLidTextColor ? ` · ${item.giftLidTextColor}` : ""}
                              </span>
                            ) : item.giftLidTextColor ? (
                              <span className="d-block lux-drawer__lid-meta">
                                Text: {item.giftLidTextColor}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        <div className="lux-drawer__controls">
                          <div className="lux-drawer__qty" role="group" aria-label="Quantity">
                            <button
                              type="button"
                              className="lux-drawer__qty-btn"
                              aria-label="Decrease quantity"
                              onClick={() =>
                                handleQuantityChange(item.id, item.quantity - 1)
                              }
                            >
                              <IoRemoveOutline size={14} aria-hidden />
                            </button>
                            <span className="lux-drawer__qty-val">{item.quantity}</span>
                            <button
                              type="button"
                              className="lux-drawer__qty-btn"
                              aria-label="Increase quantity"
                              onClick={() =>
                                handleQuantityChange(item.id, item.quantity + 1)
                              }
                            >
                              <IoAddOutline size={14} aria-hidden />
                            </button>
                          </div>
                          <Link
                            to={`/customise/${item.id}`}
                            state={customiseState(item)}
                            className="lux-drawer__edit-link"
                            onClick={onClose}
                          >
                            <BtnContent icon={IoCreateOutline} iconSize={14}>
                              {item.giftLidMessage ? "Edit message" : "Add message"}
                            </BtnContent>
                          </Link>
                          <button
                            type="button"
                            className="lux-drawer__remove"
                            onClick={() => removeFromCart(item.id)}
                          >
                            <BtnContent icon={IoTrashOutline} iconSize={14}>
                              Remove
                            </BtnContent>
                          </button>
                        </div>
                      </div>
                      <span className="lux-drawer__price">
                        ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="lux-drawer__total">
                  <span>Subtotal</span>
                  <strong>₹{total.toLocaleString("en-IN")}</strong>
                </p>
                <div className="lux-drawer__actions">
                  <Link to="/cart" className="lux-drawer__btn lux-drawer__btn--primary" onClick={onClose}>
                    <BtnContent icon={IoCartOutline} iconSize={14}>
                      View full cart
                    </BtnContent>
                  </Link>
                  <button
                    type="button"
                    className="lux-drawer__btn lux-drawer__btn--ghost"
                    onClick={() => {
                      onClose();
                      navigate("/checkout");
                    }}
                  >
                    <BtnContent icon={IoCardOutline} iconSize={14}>
                      Checkout
                    </BtnContent>
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
